import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/ui/Header';
import Sidebar from '../components/layout/Sidebar';
import Loading from '../components/ui/Loading';
import CreateTicketModal from '../components/modals/CreateTicketModal';
import useIsMobile from '../hooks/useIsMobile';

const MainLayout = ({ children }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  /** Mail modülünde yalnızca MailSidebar kullanılır; Helpdesk sol menüsü gösterilmez. */
  const hideHelpdeskSidebar = location.pathname.startsWith('/mail');
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const [prevKey, setPrevKey] = useState(location.key);

  // Global yükleme sinyallerini dinle ve gecikmeli gösterim uygula
  useEffect(() => {
    const handleLoadingEvent = (e) => {
      setIsGlobalLoading(e.detail);
    };
    window.addEventListener('app_loading', handleLoadingEvent);
    return () => window.removeEventListener('app_loading', handleLoadingEvent);
  }, []);

  // "Veri yavaş gelirse göster" mantığı (300ms threshold)
  useEffect(() => {
    let timer;
    if (isGlobalLoading) {
      // Eğer yükleme başladıysa 300ms bekle, hala bitmediyse animasyonu göster
      timer = setTimeout(() => {
        setShowLoadingUI(true);
      }, 300);
    } else {
      // Yükleme bittiyse anında gizle
      setShowLoadingUI(false);
      if (timer) clearTimeout(timer);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isGlobalLoading]);

  const handleCreateTicket = () => {
    setShowCreateModal(true);
  };

  const handleCreateTicketSubmit = async (newTicket) => {
    try {
      // Modal'ı kapat
      setShowCreateModal(false);

      // Custom event gönder - diğer component'ler dinleyebilir
      window.dispatchEvent(new CustomEvent('ticketCreated', {
        detail: newTicket
      }));

    } catch (error) {
      // Silently handle error
    }
  };

  useEffect(() => {
    if (!isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      {/* Header - Full Width Top Bar */}
      <Header
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        onMobileMenuToggle={() => setIsMobileSidebarOpen((prev) => !prev)}
        hideHelpdeskNav={hideHelpdeskSidebar}
      />

      <div className="flex flex-1 overflow-hidden h-[calc(100vh-3.5rem)] relative">
        {!hideHelpdeskSidebar && (
        <Sidebar
          onCreateTicket={handleCreateTicket}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
        )}

        {isMobile && isMobileSidebarOpen && !hideHelpdeskSidebar && (
          <button
            type="button"
            aria-label="Menüyü kapat"
            onClick={() => setIsMobileSidebarOpen(false)}
            className="fixed inset-0 z-20 bg-black/30 md:hidden"
          />
        )}

        {/* Main Content Area — mail modülünde tam yükseklik flex zinciri (sidebar kesilmesin diye) */}
        <main
          className={`flex-1 overflow-x-hidden relative ${
            hideHelpdeskSidebar
              ? 'flex flex-col min-h-0 overflow-hidden bg-gray-50 dark:bg-gray-900'
              : 'overflow-y-auto bg-gray-50/50 dark:bg-gray-900/50'
          }`}
        >
          {showLoadingUI && (
            <div className="absolute inset-0 z-50 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center">
              <Loading fullScreen={false} message="Veriler Yükleniyor..." />
            </div>
          )}
          <div
            key={location.key}
            className={`p-0 ${
              hideHelpdeskSidebar ? 'flex flex-1 flex-col min-h-0 h-full' : ''
            } ${showLoadingUI
              ? 'opacity-0 invisible'
              : 'opacity-100 visible transition-opacity duration-300'}`}
          >
            {children}
          </div>
        </main>
      </div>

      {/* Global Create Ticket Modal */}
      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateTicket={handleCreateTicketSubmit}
        selectedProject={{ id: 1, name: 'HelpDesk v2.0' }}
      />
    </div>
  );
};

export default MainLayout;
